export type EventType =
  | 'book-signing-local'
  | 'multi-author-convention'
  | 'romance-mega-signing'
  | 'virtual-event'
  | 'festival-expo'
  | 'tour-stop'
  | 'book-launch'
  | 'author-talk'
  | 'author-panel'
  | 'book-swap'
  | 'reading-meetup'
  | 'multi-author-signing'
  | 'convention-festival'
  | 'writing-workshop'
  | 'publishing-workshop'
  | 'craft-masterclass'
  | 'virtual-author-event'
  | 'livestream-discussion'
  | 'online-workshop'
  | 'reading-retreat'
  | 'midnight-release'
  | 'silent-book-club'
  | 'in-person-book-club'
  | 'virtual-book-club'
  | 'local-author-event'

export type EventStatus =
  | 'upcoming'
  | 'tickets-live'
  | 'sold-out'
  | 'postponed'
  | 'cancelled'
  | 'completed'

export type VenueType =
  | 'bookstore'
  | 'hotel'
  | 'convention-centre'
  | 'library'
  | 'restaurant'
  | 'outdoor'
  | 'other'

export type VirtualPlatform =
  | 'zoom'
  | 'youtube'
  | 'instagram-live'
  | 'facebook-live'
  | 'twitch'
  | 'other'

export type AgeRestriction = 'none' | '16+' | '18+' | '21+'

export interface TicketTier {
  id: string
  name: string
  price: number
  currency: string
  description: string
  quantity?: number
  soldOut?: boolean
}

export interface VirtualDetails {
  platform: VirtualPlatform | ''
  accessLink: string
  replayAvailable: boolean
}

export interface LocationDetails {
  venueName: string
  venueType: VenueType | ''
  streetAddress: string
  city: string
  region: string
  country: string
  postcode: string
  roomOrTable?: string
  mapLink?: string
}

export interface AccessRules {
  ticketRequired: boolean
  ticketLink?: string
  ticketTiers: TicketTier[]
  ageRestriction: AgeRestriction
  bookLimitPolicy?: string
  preorderRequired: boolean
  outsideBooksAllowed: boolean
  photosAllowed: boolean
  personalisationAllowed: boolean
}

export interface VisibilitySettings {
  showOnProfile: boolean
  showInSearch: boolean
  allowCalendar: boolean
  allowReminders: boolean
}

export interface AttendingAuthor {
  id: string
  name: string
  image: string
  genres: string[]
  tableNumber?: string
  signingSlot?: string
  books: { title: string; coverImage: string }[]
  profileLink: string
}

export interface ScheduleItem {
  id: string
  time: string
  endTime: string
  title: string
  description: string
  location?: string
  type: 'signing' | 'panel' | 'workshop' | 'social' | 'break'
  speakers?: string[]
}

export interface FullEventData {
  id: string
  eventType: EventType | ''
  eventName: string
  shortDescription: string
  longDescription?: string
  status: EventStatus
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  timezone: string
  isVirtual: boolean
  location?: LocationDetails
  virtualDetails?: VirtualDetails
  accessRules: AccessRules
  capacityLimit?: number
  estimatedAttendance?: number
  lineCutoffPolicy?: string
  visibility: VisibilitySettings
  coverImage?: string
  bannerImage?: string
  attendingAuthors?: AttendingAuthor[]
  schedule?: ScheduleItem[]
  highlights?: string[]
  faqItems?: { question: string; answer: string }[]
}

// Constants for dropdowns
export const EVENT_TYPES = [
  {
    value: 'book-signing-local',
    label: 'Book Signing – Local',
    description: 'Intimate signing at a local bookstore or venue',
  },
  {
    value: 'multi-author-convention',
    label: 'Multi-Author Convention',
    description: 'Large scale event with multiple authors and panels',
  },
  {
    value: 'romance-mega-signing',
    label: 'Romance Mega-Signing',
    description: 'Massive romance-focused signing event',
  },
  {
    value: 'virtual-event',
    label: 'Virtual Event',
    description: 'Online Q&A, reading, or launch party',
  },
  {
    value: 'festival-expo',
    label: 'Festival / Expo',
    description: 'Public literary festival or publishing expo',
  },
  {
    value: 'tour-stop',
    label: 'Tour Stop',
    description: 'Part of a larger author book tour',
  },
  {
    value: 'book-launch',
    label: 'Book Launch Event',
    description: 'Release party or launch celebration for a new book',
  },
  {
    value: 'author-talk',
    label: 'Author Talk & Reading',
    description: 'Author-led talk, reading, or presentation',
  },
  {
    value: 'author-panel',
    label: 'Author Panel & Q&A',
    description: 'Panel discussion or Q&A session with authors',
  },
  {
    value: 'book-swap',
    label: 'Book Swap',
    description: 'Community book exchange event',
  },
  {
    value: 'reading-meetup',
    label: 'Reading Meetup',
    description: 'Casual reading meetup in a social setting',
  },
  {
    value: 'multi-author-signing',
    label: 'Multi-Author Signing',
    description: 'Large signing event with multiple authors',
  },
  {
    value: 'convention-festival',
    label: 'Book Convention & Festival',
    description: 'Multi-day book convention or literary festival',
  },
  {
    value: 'writing-workshop',
    label: 'Writing Workshop',
    description: 'Hands-on writing craft workshop',
  },
  {
    value: 'publishing-workshop',
    label: 'Publishing Workshop',
    description: 'Workshop on publishing, marketing, or industry topics',
  },
  {
    value: 'craft-masterclass',
    label: 'Craft Masterclass',
    description: 'Advanced masterclass on a specific writing craft topic',
  },
  {
    value: 'virtual-author-event',
    label: 'Virtual Author Event',
    description: 'Online author Q&A, reading, or meet-and-greet',
  },
  {
    value: 'livestream-discussion',
    label: 'Livestream Book Discussion',
    description: 'Live-streamed book discussion or review session',
  },
  {
    value: 'online-workshop',
    label: 'Online Workshop',
    description: 'Virtual workshop on writing, publishing, or craft',
  },
  {
    value: 'reading-retreat',
    label: 'Reading Retreat',
    description: 'Multi-day reading retreat in a scenic location',
  },
  {
    value: 'midnight-release',
    label: 'Midnight Release Event',
    description: 'Midnight book release celebration',
  },
  {
    value: 'silent-book-club',
    label: 'Silent Book Club',
    description: 'Quiet reading session in a social setting',
  },
  {
    value: 'in-person-book-club',
    label: 'In-Person Book Club',
    description: 'Local book club meeting in person',
  },
  {
    value: 'virtual-book-club',
    label: 'Virtual Book Club',
    description: 'Online book club meeting via video call',
  },
  {
    value: 'local-author-event',
    label: 'Local Author Event',
    description: 'Event featuring local community authors',
  },
]

export const EVENT_STATUSES = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'tickets-live', label: 'Tickets Live' },
  { value: 'sold-out', label: 'Sold Out' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
]

export const VENUE_TYPES = [
  { value: 'bookstore', label: 'Bookstore' },
  { value: 'hotel', label: 'Hotel / Ballroom' },
  { value: 'convention-centre', label: 'Convention Centre' },
  { value: 'library', label: 'Library' },
  { value: 'restaurant', label: 'Restaurant / Cafe' },
  { value: 'outdoor', label: 'Outdoor Venue' },
  { value: 'other', label: 'Other' },
]

export const VIRTUAL_PLATFORMS = [
  { value: 'zoom', label: 'Zoom' },
  { value: 'youtube', label: 'YouTube Live' },
  { value: 'instagram-live', label: 'Instagram Live' },
  { value: 'facebook-live', label: 'Facebook Live' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'other', label: 'Other' },
]

export const AGE_RESTRICTIONS = [
  { value: 'none', label: 'None (All Ages)' },
  { value: '16+', label: '16+' },
  { value: '18+', label: '18+' },
  { value: '21+', label: '21+' },
]

export const COMMON_TIMEZONES = [
  { value: 'Pacific/Midway', label: 'Midway Island, Samoa' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Halifax', label: 'Atlantic Time (Canada)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires, Georgetown' },
  { value: 'Europe/London', label: 'London, Edinburgh, Lisbon' },
  { value: 'Europe/Paris', label: 'Paris, Madrid' },
  { value: 'Europe/Berlin', label: 'Berlin, Rome, Stockholm' },
  { value: 'Europe/Athens', label: 'Athens, Bucharest, Istanbul' },
  { value: 'Europe/Moscow', label: 'Moscow, St. Petersburg' },
  { value: 'Asia/Dubai', label: 'Dubai, Riyadh' },
  { value: 'Asia/Kolkata', label: 'Kuwait, Riyadh' },
  { value: 'Asia/Karachi', label: 'Nairobi' },
  { value: 'Asia/Dhaka', label: 'Islamabad, Karachi' },
  { value: 'Asia/Colombo', label: 'Dhaka, Colombo' },
  { value: 'Asia/Bangkok', label: 'Bangkok, Hanoi, Jakarta' },
  { value: 'Asia/Singapore', label: 'Singapore, Kuala Lumpur' },
  { value: 'Asia/Hong_Kong', label: 'Beijing, Chongqing, Hong Kong' },
  { value: 'Asia/Tokyo', label: 'Tokyo, Osaka, Sapporo' },
  { value: 'Asia/Seoul', label: 'Seoul' },
  { value: 'Australia/Perth', label: 'Perth' },
  { value: 'Australia/Adelaide', label: 'Perth' },
  { value: 'Australia/Darwin', label: 'Adelaide' },
  { value: 'Australia/Brisbane', label: 'Brisbane' },
  { value: 'Australia/Sydney', label: 'Sydney, Melbourne, Canberra' },
  { value: 'Pacific/Auckland', label: 'Auckland, Wellington' },
]

export const COUNTRIES = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'New Zealand',
  'Ireland',
  'South Africa',
  'Germany',
  'France',
  'Italy',
  'Spain',
  'Netherlands',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Brazil',
  'Mexico',
  'Japan',
  'South Korea',
  'Singapore',
  'India',
  'Philippines',
]
